import type { PortalBranding } from "./types";

const DEFAULT_TITLE = "Dashboard";
const DEFAULT_HEADING_FONT = "Fraunces";
const DEFAULT_BODY_FONT = "Public Sans";
const GOOGLE_FONTS_STYLESHEET_ID = "portal-brand-fonts";
const FAVICON_LINK_ID = "portal-brand-favicon";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;
const DEFAULT_FAVICON_PATH = "/static/favicon.png";

export function applyPortalBranding(
  branding?: Partial<PortalBranding>,
  fallbackName?: string,
) {
  const root = document.documentElement;
  const primary = normalizeHex(branding?.colors?.primary);
  const surfaceTint = primary ? mix(primary, "#FFFFFF", 0.97) : null;

  const primaryStrong = primary ? mix(primary, "#000000", 0.22) : "#243963";
  const primaryBase = primary ?? "#2b59bd";
  const primaryHover = primary ? mix(primary, "#000000", 0.12) : "#43506b";
  const primarySoft = primary ? mix(primary, "#FFFFFF", 0.86) : "#EAF5EC";

  const pageBg = surfaceTint ? mix(surfaceTint, "#FFFFFF", 0.1) : "#F5F0E8";
  const cardBg = surfaceTint ? mix(surfaceTint, "#FFFFFF", 0.7) : "#FFFFFF";
  const borderSubtle = surfaceTint
    ? mix(surfaceTint, "#000000", 0.1)
    : "#E8E1D4";

  const textPrimary = getReadableText(pageBg);
  const textMuted = mix(textPrimary, pageBg, 0.5);
  const textOnPrimary = getReadableText(primaryBase);

  setVar(root, "--primary", primaryBase);
  setVar(root, "--primary-mid", mix(primaryBase, "#000000", 0.18));
  setVar(root, "--primary-lt", primaryBase);
  setVar(
    root,
    "--primary-tranparent",
    opacity(mix(primaryBase, "#000000", 0.9), 0.85),
  );
  setVar(root, "--primary-shadow", opacity(primaryBase, 0.1));
  setVar(root, "--accent", primaryBase);
  setVar(root, "--accent-soft", mix(primaryBase, pageBg, 0.05));
  setVar(root, "--accent-line", mix(primaryBase, pageBg, 0.2));
  setVar(root, "--accent-text", primarySoft);
  setVar(root, "--accent-hvr", primaryHover);
  setVar(root, "--cream", pageBg);
  setVar(root, "--cream-dk", borderSubtle);
  setVar(root, "--cream-dkr", mix(borderSubtle, "#000000", 0.08));
  setVar(root, "--sand", textMuted);
  setVar(root, "--sand-dk", mix(textMuted, "#000000", 0.5));
  setVar(root, "--sand-lt", mix(textMuted, pageBg, 0.75));
  setVar(root, "--text", textPrimary);
  setVar(root, "--white", cardBg);
  setVar(root, "--active", primarySoft);
  setVar(root, "--primary-strong", primaryStrong);
  setVar(root, "--primary-hover", primaryHover);
  setVar(root, "--primary-soft", primarySoft);
  setVar(root, "--page-bg", pageBg);
  setVar(root, "--card-bg", cardBg);
  setVar(root, "--border-subtle", borderSubtle);
  setVar(root, "--text-primary", textPrimary);
  setVar(root, "--text-muted", textMuted);
  setVar(root, "--text-on-primary", textOnPrimary);
  setVar(root, "--success", "#2F7D4A");
  setVar(root, "--warning", "#C97B1A");
  setVar(root, "--danger", "#B83232");

  const headingFont = branding?.fonts?.heading?.trim() || DEFAULT_HEADING_FONT;
  const bodyFont = branding?.fonts?.body?.trim() || DEFAULT_BODY_FONT;
  setVar(root, "--font-h", toFontStack(headingFont, true));
  setVar(root, "--font-b", toFontStack(bodyFont, false));
  syncGoogleFontsStylesheet([headingFont, bodyFont]);

  const brandName = branding?.brandName || fallbackName;
  const brandTagline = branding?.brandTagline;
  document.title = brandName
    ? brandTagline
      ? `${brandName} - ${brandTagline}`
      : brandName
    : DEFAULT_TITLE;

  syncFavicon(branding?.faviconUrl ?? getDefaultFaviconUrl());
}

function setVar(root: HTMLElement, name: string, value: string) {
  root.style.setProperty(name, value);
}

function syncGoogleFontsStylesheet(fontFamilies: string[]) {
  const families = [
    ...new Set(fontFamilies.map((value) => value.trim()).filter(Boolean)),
  ];
  if (families.length === 0) return;

  const href = buildGoogleFontsHref(families);
  let link = document.getElementById(
    GOOGLE_FONTS_STYLESHEET_ID,
  ) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.id = GOOGLE_FONTS_STYLESHEET_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }

  if (link.href !== href) {
    link.href = href;
  }
}

function getDefaultFaviconUrl() {
  if (!API_BASE_URL) return undefined;

  return new URL(DEFAULT_FAVICON_PATH, API_BASE_URL).toString();
}

function syncFavicon(url?: string) {
  if (!url) return;

  let link = document.getElementById(FAVICON_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = FAVICON_LINK_ID;
    link.rel = "icon";
    document.head.appendChild(link);
  }

  link.href = url;
}

function buildGoogleFontsHref(fontFamilies: string[]) {
  const baseUrl = "https://fonts.googleapis.com/css2";
  const params = new URLSearchParams();

  fontFamilies.forEach((family) => {
    params.append(
      "family",
      `${family.replace(/ /g, "+")}:wght@300;400;500;600;700`,
    );
  });
  params.set("display", "swap");

  return `${baseUrl}?${params.toString()}`;
}

function toFontStack(fontFamily: string, isHeading: boolean) {
  const fallback = isHeading ? "Georgia, serif" : "sans-serif";
  return `'${fontFamily}', ${fallback}`;
}

function normalizeHex(value?: string): string | undefined {
  if (!value) return undefined;
  const hex = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return undefined;
}

function mix(colorA: string, colorB: string, weightOfB: number) {
  const colorOne = hexToRgb(colorA);
  const colorTwo = hexToRgb(colorB);
  const weightOfA = 1 - weightOfB;
  return rgbToHex({
    r: Math.round(colorOne.r * weightOfA + colorTwo.r * weightOfB),
    g: Math.round(colorOne.g * weightOfA + colorTwo.g * weightOfB),
    b: Math.round(colorOne.b * weightOfA + colorTwo.b * weightOfB),
  });
}
function opacity(color: string, opacity: number) {
  const { r, g, b } = hexToRgb(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function getReadableText(bgHex: string) {
  return getContrastRatio(bgHex, "#111111") >=
    getContrastRatio(bgHex, "#FFFFFF")
    ? "#111111"
    : "#FFFFFF";
}

function getContrastRatio(colorA: string, colorB: string) {
  const luminanceOne = getLuminance(hexToRgb(colorA));
  const luminanceTwo = getLuminance(hexToRgb(colorB));
  const lighter = Math.max(luminanceOne, luminanceTwo);
  const darker = Math.min(luminanceOne, luminanceTwo);
  return (lighter + 0.05) / (darker + 0.05);
}

function getLuminance(rgb: { r: number; g: number; b: number }) {
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function hexToRgb(hex: string) {
  const value = hex.replace("#", "");
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHex(rgb: { r: number; g: number; b: number }) {
  return `#${[rgb.r, rgb.g, rgb.b]
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
}
